import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Tab,
  styled,
  TypographyProps,
} from "@mui/material";
import { format } from "date-fns";
import { useMemo, memo } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import  { keyframes } from 'styled-components';


// Hooks
import { useMatches } from "./hooks/useMatches";
import { TabsValues, GroupedMatches } from "./types";
import { MatchData } from "@/interfaces/liveGamesType";

// Componentes
import LeaguesList from "./components/LeaguesList";
import MatchesList from "./components/MatchesList";
import MatchDetails from "./components/MatchDetails";
import DateNavigation from "./components/DateNavigation";
import NeonTabs, { TabPanel, a11yProps } from '@/components/NeonTabs';
import ScrollTopButton from "@/components/ScrollTopButton";

// Animações
const pulse = keyframes`
  0% {
    opacity: 1;
    box-shadow: 0 0 20px rgba(42, 244, 151, 0.3);
  }
  50% {
    opacity: 0.9;
    box-shadow: 0 0 40px rgba(42, 244, 151, 0.6);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 20px rgba(42, 244, 151, 0.3);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const gridAnimation = keyframes`
  0% {
    background-position: 0 0;
    opacity: 0.2;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    background-position: 100% 100%;
    opacity: 0.2;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Componentes estilizados
const NeonPaper = styled(Paper)`
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.2);
  backdrop-filter: blur(5px);
  animation: ${pulse} 4s infinite ease-in-out;
  transition: all 0.3s ease;
  border-radius: 10px;
  &:hover {
    box-shadow: 0 0 15px rgba(42, 244, 151, 0.4);
  }
`;

const NeonText = styled(Typography)<TypographyProps>`
  color: #fff;
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.7);
  background: linear-gradient(90deg, #ffffff, #2af497);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 4s linear infinite;
`;

const FloatingContainer = styled(Box)`
  animation: ${float} 5s ease infinite;
`;

// Define proper types for TabContent component props
interface TabContentProps {
  leagues: any[];
  filteredMatches: MatchData[];
  groupedMatches: GroupedMatches;
  selectedMatch: MatchData | null;
  toggleFavorite: (id: string) => void;
  selectMatch: (match: MatchData) => void;
  isMobile: boolean;
  openDetailsMobile: boolean;
  closeMobileDetails: () => void;
  selectedLeagueId: string | null;
  selectLeague: (leagueId: string) => void;
  favorites: string[];
  loading: boolean;
}

// Memoized TabContent component to prevent unnecessary re-renders
const TabContent = memo(({ 
  leagues, 
  filteredMatches, 
  groupedMatches, 
  selectedMatch, 
  toggleFavorite, 
  selectMatch, 
  isMobile, 
  openDetailsMobile, 
  closeMobileDetails, 
  selectedLeagueId, 
  selectLeague, 
  favorites, 
  loading 
}: TabContentProps) => (
  <Grid container spacing={1}>
    {/* Coluna Esquerda - Ligas e Classificações */}
    <Grid
      item
      xs={12}
      md={3}
      sx={{ display: { xs: isMobile ? "none" : "flex", md: "flex" } }}
    >
      <NeonPaper
        sx={{
          height: "600px",
          display: "flex",
          width: "100%",
          flexDirection: "column",
          overflow: "hidden",
          p: 0.5
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            width: "100%",
            height: "100%",
            ".MuiPaper-root": {
              height: "100%",
            },
          }}
        >
          <LeaguesList
            leagues={leagues}
            favorites={favorites}
            toggleFavorite={toggleFavorite}
            matches={filteredMatches}
            selectedLeagueId={selectedLeagueId}
            onSelectLeague={selectLeague}
            isLoading={loading}
          />
        </Box>
      </NeonPaper>
    </Grid>

    {/* Coluna Central - Lista de Jogos */}
    <Grid
      item
      xs={12}
      md={isMobile ? (selectedMatch && openDetailsMobile ? 0 : 12) : 5}
    >
      <NeonPaper
        sx={{
          height: !isMobile ? "600px" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: !isMobile ? "hidden" : "visible",
          p: 0.5
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: !isMobile ? "auto" : "visible",
            width: "100%",
            height: !isMobile ? "100%" : "auto",
            ".MuiPaper-root": {
              height: "100%",
            },
          }}
        >
          <MatchesList
            matches={filteredMatches}
            loading={loading}
            selectedMatch={selectedMatch}
            favorites={favorites}
            groupedMatches={groupedMatches}
            onSelectMatch={selectMatch}
            toggleFavorite={toggleFavorite}
            isMobile={isMobile}
          />
        </Box>
      </NeonPaper>
    </Grid>

    {/* Coluna Direita - Detalhes da Partida (versão desktop) */}
    <Grid
      item
      xs={12}
      md={isMobile ? 0 : 4}
      sx={{
        display: isMobile ? "none" : selectedMatch ? "block" : "none",
        height: "auto",
      }}
    >
      <NeonPaper
        sx={{
          height: "600px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: 0.5
          }}
        >
          <MatchDetails
            match={selectedMatch}
            onClose={closeMobileDetails}
            isMobile={isMobile}
          />
        </Box>
      </NeonPaper>
    </Grid>
  </Grid>
));

export default function DashboardV2() {
  // Usar o hook de partidas
  const {
    state,
    loading,
    filteredMatches,
    groupedMatches,
    handleTabChange,
    changeDate,
    handleSearch,
    toggleFavorite,
    selectMatch,
    isMobile,
    openDetailsMobile,
    closeMobileDetails,
    selectSpecificDate,
    selectLeague,
  } = useMatches();

  // Extrair valores do estado
  const {
    date,
    tabValue,
    leagues,
    liveMatches,
    selectedMatch,
    favorites,
    searchQuery,
    selectedLeagueId,
  } = state;

  // Animações de scroll
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Memoize tab panels to prevent unnecessary re-renders
  const tabPanels = useMemo(() => {
    const tabContentProps = {
      leagues,
      filteredMatches,
      groupedMatches,
      selectedMatch,
      toggleFavorite,
      selectMatch,
      isMobile,
      openDetailsMobile,
      closeMobileDetails,
      selectedLeagueId,
      selectLeague,
      favorites,
      loading
    };

    return Object.values(TabsValues).map(index => (
      <TabPanel key={index} value={tabValue} index={index} prefix="dashboard">
        <TabContent {...tabContentProps} />
      </TabPanel>
    ));
  }, [tabValue, leagues, filteredMatches, groupedMatches, selectedMatch, 
      toggleFavorite, selectMatch, isMobile, openDetailsMobile, 
      closeMobileDetails, selectedLeagueId, selectLeague, favorites, loading]);

  return (
    <>
      <div className="dashboard-v2-container">
        <motion.div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #2af497, #1ce488)',
            transformOrigin: '0%',
            scaleX
          }}
        />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Cabeçalho */}
          <FloatingContainer
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <NeonText variant="h5" component="h1" fontWeight="bold">
              Futebol
            </NeonText>
            <Box display="flex" alignItems="center">
              <NeonText variant="body2" component="span" sx={{ mr: 1 }}>
                {format(date, "dd/MM/yyyy")}
              </NeonText>
            </Box>
          </FloatingContainer>

          {/* Barra de navegação de datas e pesquisa */}
          <DateNavigation
            date={date}
            searchQuery={searchQuery}
            onDateChange={changeDate}
            onSearchChange={handleSearch}
            onSpecificDateChange={selectSpecificDate}
          />

          {/* Tabs de filtro */}
          <NeonTabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Todos" {...a11yProps(0, "dashboard")} />
            <Tab label={`Ao Vivo (${liveMatches.length})`} {...a11yProps(1, "dashboard")} />
            <Tab label="Finalizados" {...a11yProps(2, "dashboard")} />
            <Tab label="Favoritos" {...a11yProps(3, "dashboard")} />
          </NeonTabs>

          {/* Conteúdo baseado na tab selecionada */}
          {tabPanels}

          {/* Versão mobile para detalhes da partida com animação */}
          {isMobile && selectedMatch && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ 
                y: openDetailsMobile ? 0 : "100%",
                transition: { duration: 0.3, ease: "easeInOut" }
              }}
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 1500,
                background: "rgba(20, 20, 25, 0.95)",
                backdropFilter: "blur(10px)",
                borderTop: "1px solid rgba(42, 244, 151, 0.5)",
                boxShadow: "0 -5px 25px rgba(42, 244, 151, 0.3)",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  overflow: "auto",
                  p: 2,
                }}
              >
                <MatchDetails
                  match={selectedMatch}
                  onClose={closeMobileDetails}
                  isMobile={isMobile}
                />
              </Box>
            </motion.div>
          )}

          {/* Botão para voltar ao topo - agora usando o componente global */}
          <ScrollTopButton />
        </Container>
      </div>
    </>
  );
}
